/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken, selectAuthLoading, selectRefreshToken, selectTokenRevocation } from '../auth/selectors';
import { authActions } from '../auth/slice';
import TIME_OUTS from '../constants/time-outs';
import { selectIsSessionTimedOut } from '../../visual/selectors';
import useIsTokenValid from '../auth/hooks/use-access-token-validity';
import useIsAuthenticated from '../auth/hooks/use-is-authenticated';
import { visualActions } from '../../visual/slice';

const useUpdateToken = () => {
  const dispatch = useDispatch();
  //
  const isSessionTimedOut = useSelector(selectIsSessionTimedOut);
  const isAuthLoading = useSelector(selectAuthLoading);
  const isTokenRevoked = useSelector(selectTokenRevocation);
  const accessToken = useSelector(selectAccessToken);
  const refreshToken = useSelector(selectRefreshToken);
  const { checkAuthentication } = useIsAuthenticated();
  const { doNeedRefresh } = useIsTokenValid();
  //
  const tryUpdateToken = useCallback(async () => {
    dispatch(authActions.refreshToken({ refreshToken }));
  }, [refreshToken]);
  //
  useEffect(() => {
    // session observer
    const interval = setInterval(() => {
      if (checkAuthentication(refreshToken)) {
        // check whether access token need to refresh
        if (doNeedRefresh(accessToken)) {
          if (!isSessionTimedOut && !isTokenRevoked && !isAuthLoading) {
            tryUpdateToken();
          }
        }

        // session has been timed out
      } else if (!isSessionTimedOut) {
        dispatch(visualActions.setSessionTimeOut(true));
      }
    }, TIME_OUTS.SESSION_OBSERVER_TIME);
    return () => {
      clearInterval(interval);
    };
  }, [isSessionTimedOut, isAuthLoading, isTokenRevoked, accessToken, refreshToken]);
};
//
export default useUpdateToken;
